import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import { createMakeupArtist } from '../../services/makeupArtistService';
import { registerUser, checkAvailability } from '../../services/authService';
import { validateSignupFields, mergeAvailabilityErrors, hasErrors, FieldErrors } from '../../utils/validationUtils';

const ARTIST_ROLE_ID = 2; // As per the requirements

const ArtistSignup: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  // Artist Profile
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [artistTypeId, setArtistTypeId] = useState<number | ''>('');
    const [artistTypes, setArtistTypes] = useState<{ artistTypeId: number; artistTypeName: string }[]>([]);
  useEffect(() => {
    // Load artist types for dropdown
    import('../../services/artistTypeService').then(mod => {
      mod.getArtistTypes()
        .then(types => setArtistTypes(types))
        .catch(() => setArtistTypes([]));
    });
  }, []);

  // Account Details
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [phone, setPhone] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setFieldErrors({});

    // Frontend format validation (no uniqueness yet)
    const baseErrors = validateSignupFields({
      username,
      email,
      phone,
      displayName,
      bio,
    });
    if (hasErrors(baseErrors)) {
      setFieldErrors(baseErrors);
      setLoading(false);
      setError('Please fix the highlighted errors.');
      return;
    }

    // Attempt availability check (if backend endpoint exists)
    const availability = await checkAvailability({
      username: username.toLowerCase(),
      email: email.toLowerCase(),
      phone: `${countryCode}${phone}`,
    });
    const availabilityErrors = mergeAvailabilityErrors(baseErrors, availability);
    if (hasErrors(availabilityErrors)) {
      setFieldErrors(availabilityErrors);
      setLoading(false);
      setError('Some fields are already in use.');
      return;
    }

    try {
      // Step 1: Create artist profile (inactive by default)
      const artist = await createMakeupArtist({
        displayName,
        bio,
        active: false,
        artistTypeId: artistTypeId || undefined,
      });

      // Step 2: Register user account with default password
      await registerUser({
        username: username.toLowerCase(),
        password: '00000',
        fullName: displayName,
        email: email.toLowerCase(),
        phone: `${countryCode}${phone}`, // Combine country code with phone number
        roleId: ARTIST_ROLE_ID,
        makeupArtistId: artist.makeupArtistId,
      });

      // Step 3: Navigate to success page (skip plan selection - no payment gateway yet)
      navigate('/signup/success', {
        state: {
          artistName: displayName,
          email,
        },
      });
    } catch (err: any) {
      // Attempt to map conflict messages to field errors
      const rawMsg = err?.response?.data?.message || err?.message || 'Signup failed. Please try again.';
      let msg = String(rawMsg);
      const lower = msg.toLowerCase();
      const fe: FieldErrors = { ...fieldErrors };
      if (lower.includes('username')) fe.username = fe.username || 'Username already taken';
      if (lower.includes('email')) fe.email = fe.email || 'Email already registered';
      if (lower.includes('phone')) fe.phone = fe.phone || 'Phone already registered';
      setFieldErrors(fe);
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-16 bg-background">
      <div className="max-w-2xl mx-auto py-12 px-4 flex-1">
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-secondary/20">
          <h1 className="text-3xl font-semibold text-center mb-2 text-primary">Artist Signup</h1>
          <p className="text-center text-secondary mb-8">Join ArtistDesk and grow your business</p>

          {error && (
            <div className="bg-red-100 text-red-600 p-3 mb-6 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Artist Profile Section */}
            <div className="border-b border-secondary/10 pb-6">
              <h2 className="text-xl font-semibold text-secondary mb-4">Artist Profile</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    placeholder="Your professional name"
                    required
                  />
                  {fieldErrors.displayName && <p className="text-red-600 text-xs mt-1">{fieldErrors.displayName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Bio <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    placeholder="Tell us about your expertise and experience"
                    rows={4}
                    required
                  />
                  {fieldErrors.bio && <p className="text-red-600 text-xs mt-1">{fieldErrors.bio}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Artist Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={artistTypeId}
                    onChange={e => setArtistTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    required
                  >
                    <option value="">Select artist type</option>
                    {artistTypes.map(type => (
                      <option key={type.artistTypeId} value={type.artistTypeId}>{type.artistTypeName}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Account Details Section */}
            <div>
              <h2 className="text-xl font-semibold text-secondary mb-4">Account Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    placeholder="Choose a unique username"
                    required
                  />
                  {fieldErrors.username && <p className="text-red-600 text-xs mt-1">{fieldErrors.username}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    placeholder="your@email.com"
                    required
                  />
                  {fieldErrors.email && <p className="text-red-600 text-xs mt-1">{fieldErrors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                    >
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+61">+61 (AU)</option>
                      <option value="+971">+971 (AE)</option>
                      <option value="+65">+65 (SG)</option>
                      <option value="+86">+86 (CN)</option>
                      <option value="+81">+81 (JP)</option>
                      <option value="+82">+82 (KR)</option>
                      <option value="+33">+33 (FR)</option>
                      <option value="+49">+49 (DE)</option>
                      <option value="+39">+39 (IT)</option>
                      <option value="+34">+34 (ES)</option>
                      <option value="+7">+7 (RU)</option>
                      <option value="+55">+55 (BR)</option>
                      <option value="+27">+27 (ZA)</option>
                      <option value="+20">+20 (EG)</option>
                      <option value="+234">+234 (NG)</option>
                      <option value="+254">+254 (KE)</option>
                      <option value="+92">+92 (PK)</option>
                      <option value="+880">+880 (BD)</option>
                      <option value="+94">+94 (LK)</option>
                      <option value="+977">+977 (NP)</option>
                      <option value="+60">+60 (MY)</option>
                      <option value="+63">+63 (PH)</option>
                      <option value="+64">+64 (NZ)</option>
                      <option value="+52">+52 (MX)</option>
                      <option value="+54">+54 (AR)</option>
                      <option value="+56">+56 (CL)</option>
                      <option value="+57">+57 (CO)</option>
                    </select>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                      placeholder="1234567890"
                      required
                    />
                  </div>
                  {fieldErrors.phone && <p className="text-red-600 text-xs mt-1">{fieldErrors.phone}</p>}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Complete Signup'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-secondary">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary hover:underline font-medium"
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ArtistSignup;
